import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  getApps,
  getLiteLLMKeyUsage,
  getSessionPrompts,
  getSessions,
  getStats,
  getUsers,
} from "../utils/api";
import type {
  AppRow,
  AppStatus,
  AppsFilterRequest,
  LiteLLMKeyRow,
  LiteLLMKeyUsageFilterRequest,
  PromptRow,
  SessionRow,
  SessionsFilterRequest,
  StatsResponse,
  UserRow,
} from "../utils/types";

interface SharedFilters {
  start_date?: string;
  end_date?: string;
}

interface SessionFilters {
  user_email: string | "all";
  search: string;
  page: number;
  page_size: number;
}

interface AppFilters {
  status: AppStatus | "all";
  user_email: string | "all";
  search: string;
  page: number;
  page_size: number;
}

interface KeyUsageFilters {
  search: string;
  page: number;
  page_size: number;
}

interface ClaudeCodeContextType {
  // Stats
  stats: StatsResponse | null;
  statsLoading: boolean;

  // Users (used to populate filter dropdowns)
  users: UserRow[];
  usersLoading: boolean;

  // Sessions table
  sessions: SessionRow[];
  sessionsLoading: boolean;
  sessionsTotal: number;

  // Prompts for the selected session (sheet)
  prompts: PromptRow[];
  promptsLoading: boolean;

  // Apps
  apps: AppRow[];
  appsLoading: boolean;
  appsTotal: number;

  // LiteLLM key usage
  keyUsage: LiteLLMKeyRow[];
  keyUsageLoading: boolean;
  keyUsageTotal: number;

  // Selection (drives sheet)
  selectedSessionId: string | null;
  setSelectedSessionId: (id: string | null) => void;
  selectedSession: SessionRow | null;

  // Filters
  sharedFilters: SharedFilters;
  setSharedFilters: (f: SharedFilters) => void;
  sessionFilters: SessionFilters;
  setSessionFilters: (f: SessionFilters) => void;
  appFilters: AppFilters;
  setAppFilters: (f: AppFilters) => void;
  keyUsageFilters: KeyUsageFilters;
  setKeyUsageFilters: (f: KeyUsageFilters) => void;

  // Actions
  refreshAll: () => Promise<void>;
  refreshSessions: () => Promise<void>;
  refreshApps: () => Promise<void>;
  refreshKeyUsage: () => Promise<void>;
}

const ClaudeCodeContext = createContext<ClaudeCodeContextType | undefined>(
  undefined
);

const toIsoDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Default to "this week" — Monday through today (local time).
const getThisWeekRange = (): SharedFilters => {
  const today = new Date();
  const dow = today.getDay(); // 0 = Sun, 1 = Mon, ...
  const offsetToMonday = dow === 0 ? 6 : dow - 1;
  const monday = new Date(today);
  monday.setDate(today.getDate() - offsetToMonday);
  return { start_date: toIsoDate(monday), end_date: toIsoDate(today) };
};

const defaultSharedFilters: SharedFilters = getThisWeekRange();

const defaultSessionFilters: SessionFilters = {
  user_email: "all",
  search: "",
  page: 0,
  page_size: 25,
};

const defaultAppFilters: AppFilters = {
  status: "all",
  user_email: "all",
  search: "",
  page: 0,
  page_size: 25,
};

const defaultKeyUsageFilters: KeyUsageFilters = {
  search: "",
  page: 0,
  page_size: 25,
};

export function ClaudeCodeProvider({ children }: { children: ReactNode }) {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [users, setUsers] = useState<UserRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const [prompts, setPrompts] = useState<PromptRow[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);

  const [apps, setApps] = useState<AppRow[]>([]);
  const [appsTotal, setAppsTotal] = useState(0);
  const [appsLoading, setAppsLoading] = useState(false);

  const [keyUsage, setKeyUsage] = useState<LiteLLMKeyRow[]>([]);
  const [keyUsageTotal, setKeyUsageTotal] = useState(0);
  const [keyUsageLoading, setKeyUsageLoading] = useState(false);

  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null
  );

  const [sharedFilters, setSharedFilters] =
    useState<SharedFilters>(defaultSharedFilters);
  const [sessionFilters, setSessionFilters] =
    useState<SessionFilters>(defaultSessionFilters);
  const [appFilters, setAppFilters] = useState<AppFilters>(defaultAppFilters);
  const [keyUsageFilters, setKeyUsageFilters] = useState<KeyUsageFilters>(
    defaultKeyUsageFilters
  );

  const selectedSession = useMemo(
    () => sessions.find((s) => s.session_id === selectedSessionId) || null,
    [sessions, selectedSessionId]
  );

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await getStats();
      setStats(data);
    } catch {
      // Toastify handled in api layer
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const data = await getUsers();
      setUsers(data || []);
    } catch {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const filters: SessionsFilterRequest = {
        user_email:
          sessionFilters.user_email === "all"
            ? undefined
            : sessionFilters.user_email,
        search: sessionFilters.search || undefined,
        start_date: sharedFilters.start_date,
        end_date: sharedFilters.end_date,
        page: sessionFilters.page + 1,
        page_size: sessionFilters.page_size,
      };
      const res = await getSessions(filters);
      setSessions(res.data || []);
      setSessionsTotal(res.total_count || 0);
    } catch {
      setSessions([]);
      setSessionsTotal(0);
    } finally {
      setSessionsLoading(false);
    }
  }, [sessionFilters, sharedFilters]);

  const fetchPrompts = useCallback(async () => {
    if (!selectedSessionId) {
      setPrompts([]);
      return;
    }
    setPromptsLoading(true);
    try {
      const data = await getSessionPrompts(
        selectedSessionId,
        selectedSession?.user_email
      );
      setPrompts(data || []);
    } catch {
      setPrompts([]);
    } finally {
      setPromptsLoading(false);
    }
  }, [selectedSessionId, selectedSession?.user_email]);

  const fetchApps = useCallback(async () => {
    setAppsLoading(true);
    try {
      const filters: AppsFilterRequest = {
        status: appFilters.status,
        user_email:
          appFilters.user_email === "all" ? undefined : appFilters.user_email,
        search: appFilters.search || undefined,
        start_date: sharedFilters.start_date,
        end_date: sharedFilters.end_date,
        page: appFilters.page + 1,
        page_size: appFilters.page_size,
      };
      const res = await getApps(filters);
      setApps(res.data || []);
      setAppsTotal(res.total_count || 0);
    } catch {
      setApps([]);
      setAppsTotal(0);
    } finally {
      setAppsLoading(false);
    }
  }, [appFilters, sharedFilters]);

  const fetchKeyUsage = useCallback(async () => {
    setKeyUsageLoading(true);
    try {
      const filters: LiteLLMKeyUsageFilterRequest = {
        search: keyUsageFilters.search || undefined,
        start_date: sharedFilters.start_date,
        end_date: sharedFilters.end_date,
        page: keyUsageFilters.page + 1,
        page_size: keyUsageFilters.page_size,
      };
      const res = await getLiteLLMKeyUsage(filters);
      setKeyUsage(res.data || []);
      setKeyUsageTotal(res.total_count || 0);
    } catch {
      setKeyUsage([]);
      setKeyUsageTotal(0);
    } finally {
      setKeyUsageLoading(false);
    }
  }, [keyUsageFilters, sharedFilters]);

  useEffect(() => {
    fetchStats();
    fetchUsers();
  }, [fetchStats, fetchUsers]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  useEffect(() => {
    fetchKeyUsage();
  }, [fetchKeyUsage]);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      fetchStats(),
      fetchUsers(),
      fetchSessions(),
      fetchApps(),
      fetchKeyUsage(),
    ]);
    if (selectedSessionId) {
      await fetchPrompts();
    }
  }, [
    fetchStats,
    fetchUsers,
    fetchSessions,
    fetchApps,
    fetchKeyUsage,
    fetchPrompts,
    selectedSessionId,
  ]);

  const value: ClaudeCodeContextType = {
    stats,
    statsLoading,
    users,
    usersLoading,
    sessions,
    sessionsTotal,
    sessionsLoading,
    prompts,
    promptsLoading,
    apps,
    appsTotal,
    appsLoading,
    keyUsage,
    keyUsageTotal,
    keyUsageLoading,
    selectedSessionId,
    setSelectedSessionId,
    selectedSession,
    sharedFilters,
    setSharedFilters,
    sessionFilters,
    setSessionFilters,
    appFilters,
    setAppFilters,
    keyUsageFilters,
    setKeyUsageFilters,
    refreshAll,
    refreshSessions: fetchSessions,
    refreshApps: fetchApps,
    refreshKeyUsage: fetchKeyUsage,
  };

  return (
    <ClaudeCodeContext.Provider value={value}>
      {children}
    </ClaudeCodeContext.Provider>
  );
}

export function useClaudeCode() {
  const ctx = useContext(ClaudeCodeContext);
  if (!ctx) {
    throw new Error("useClaudeCode must be used within ClaudeCodeProvider");
  }
  return ctx;
}
